import type { MouseEvent as ReactMouseEvent } from 'react'
import { useCallback, useEffect, useState } from 'react'
import type { SunoPrompt } from '../../../api/suno'
import { emptyPromptEditor, type PromptEditorState } from '../PromptEditorDialog'
import type { PromptActionMenuPosition } from './PromptManagementItems'

type PromptLibraryDialogsOptions = {
  toast: (message: string) => void
}

const promptToEditorState = (prompt: SunoPrompt): PromptEditorState => ({
  id: prompt.id,
  title: prompt.title,
  description: prompt.description ?? '',
  groupIds: prompt.groups.map((group) => group.id),
  stylePrompt: prompt.style_prompt,
  excludePrompt: prompt.exclude_prompt ?? ''
})

export const usePromptLibraryDialogs = ({ toast }: PromptLibraryDialogsOptions) => {
  const [promptEditor, setPromptEditor] = useState<PromptEditorState | null>(null)
  const [promptToDelete, setPromptToDelete] = useState<SunoPrompt | null>(null)
  const [promptTracksDrawerPrompt, setPromptTracksDrawerPrompt] = useState<SunoPrompt | null>(null)
  const [promptSimilarityPrompt, setPromptSimilarityPrompt] = useState<SunoPrompt | null>(null)
  const [promptActionMenuId, setPromptActionMenuId] = useState<string | null>(null)
  const [promptActionMenuPosition, setPromptActionMenuPosition] = useState<PromptActionMenuPosition | null>(null)

  useEffect(() => {
    if (!promptActionMenuId) return

    const closeMenu = (): void => {
      setPromptActionMenuId(null)
      setPromptActionMenuPosition(null)
    }
    const closeOnEscape = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        closeMenu()
      }
    }

    document.addEventListener('mousedown', closeMenu)
    document.addEventListener('keydown', closeOnEscape)

    return () => {
      document.removeEventListener('mousedown', closeMenu)
      document.removeEventListener('keydown', closeOnEscape)
    }
  }, [promptActionMenuId])

  const openCreatePromptDialog = useCallback((): void => {
    setPromptEditor(emptyPromptEditor())
  }, [])

  const openEditPromptDialog = useCallback((prompt: SunoPrompt): void => {
    setPromptEditor(promptToEditorState(prompt))
  }, [])

  const copyText = useCallback(async (value: string, label: string): Promise<void> => {
    await navigator.clipboard.writeText(value)
    toast(`Copied ${label}.`)
  }, [toast])

  const togglePromptActionMenu = (event: ReactMouseEvent<HTMLButtonElement>, menuId: string): void => {
    if (promptActionMenuId === menuId) {
      setPromptActionMenuId(null)
      setPromptActionMenuPosition(null)
      return
    }

    const rect = event.currentTarget.getBoundingClientRect()
    const menuWidth = 210
    const menuMaxHeight = 360
    const gap = 6
    const left = Math.max(8, Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 8))
    const opensAbove = rect.bottom + gap + menuMaxHeight > window.innerHeight && rect.top > menuMaxHeight

    setPromptActionMenuId(menuId)
    setPromptActionMenuPosition({
      left,
      placement: opensAbove ? 'above' : 'below',
      top: opensAbove ? rect.top - gap : rect.bottom + gap
    })
  }

  const closePromptActionMenu = (): void => {
    setPromptActionMenuId(null)
    setPromptActionMenuPosition(null)
  }

  return {
    promptEditor,
    promptToDelete,
    promptTracksDrawerPrompt,
    promptSimilarityPrompt,
    promptActionMenuId,
    promptActionMenuPosition,
    closePromptActionMenu,
    closePromptEditor: () => setPromptEditor(null),
    closePromptSimilarity: () => setPromptSimilarityPrompt(null),
    closePromptTracksDrawer: () => setPromptTracksDrawerPrompt(null),
    closePromptDeleteDialog: () => setPromptToDelete(null),
    copyText,
    openCreatePromptDialog,
    openDeletePromptDialog: setPromptToDelete,
    openEditPromptDialog,
    openPromptSimilarity: setPromptSimilarityPrompt,
    openPromptTracksDrawer: setPromptTracksDrawerPrompt,
    setPromptEditor,
    togglePromptActionMenu
  }
}
